/**
 * Pure helpers for the home "Book a room" sheet: start-time options on a
 * 15-min grid within building hours, and how well each room fits.
 *
 * Day/time strings are the BUILDING's wall clock (`YYYY-MM-DD`, `HH:MM`).
 */
import type { BuildingHours, FreeRoomView } from '@hub-wifi/shared'
import { nowHmInTz, roundUpToSlot, todayYmdInTz } from '../../lib/calendar-time'

/** Duration chips, in minutes. 30 min is also the booking service minimum. */
export const DURATIONS = [30, 60, 90, 120] as const
export const DEFAULT_DURATION = 60
export const SLOT_MINUTES = 15
const MIN_BOOKING_MINUTES = 30
/** Used when the building has no (or overnight) hours for the day: the whole
 *  local day, matching the server, which then caps the window at midnight. */
const FALLBACK_OPEN = '00:00'
const FALLBACK_CLOSE = '24:00'

export type WhenMode = 'now' | 'later' | 'date'

export function hmToMinutes(hm: string): number {
  const [h, m] = hm.split(':').map(Number) as [number, number]
  return h * 60 + m
}

export function minutesToHm(total: number): string {
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

function weekdayOf(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay()
}

/** Opening hours for a building-local day; `null` when it's closed. */
export function hoursForDay(
  hours: BuildingHours[],
  ymd: string,
): { open: string; close: string } | null {
  const entry = hours.find((h) => h.dayOfWeek === weekdayOf(ymd))
  if (entry?.isClosed) return null
  if (!entry || !entry.openTime || !entry.closeTime || entry.closeTime <= entry.openTime) {
    return { open: FALLBACK_OPEN, close: FALLBACK_CLOSE }
  }
  return { open: entry.openTime, close: entry.closeTime }
}

/** Lead time before rounding, so "Now" never lands on the current minute:
 *  the booking service rejects a start in the past, and the request takes a
 *  moment to arrive. */
const NOW_LEAD_MS = 60_000

/**
 * "Now": rounded up to the next 15 min, at least a minute ahead (16:14:30 and
 * 16:15:00 both give 16:30). `null` when that would pass midnight.
 */
export function nowStart(timeZone: string, now: Date): { ymd: string; hm: string } | null {
  // Ceil to the whole minute so the start is always >= NOW_LEAD_MS ahead.
  const lead = new Date(Math.ceil((now.getTime() + NOW_LEAD_MS) / 60_000) * 60_000)
  const hm = roundUpToSlot(nowHmInTz(timeZone, lead), SLOT_MINUTES)
  return hm ? { ymd: todayYmdInTz(timeZone, lead), hm } : null
}

/**
 * Start-time options for a day: a 15-min grid from opening until the last
 * start that still fits the shortest booking before closing. Today only
 * offers times from "now" (rounded up). Empty when the building is closed.
 */
export function startSlots(
  hours: BuildingHours[],
  ymd: string,
  timeZone: string,
  now: Date,
): string[] {
  const day = hoursForDay(hours, ymd)
  if (!day) return []
  let first = Math.ceil(hmToMinutes(day.open) / SLOT_MINUTES) * SLOT_MINUTES
  const last = hmToMinutes(day.close) - MIN_BOOKING_MINUTES
  if (ymd === todayYmdInTz(timeZone, now)) {
    const start = nowStart(timeZone, now)
    if (!start) return []
    first = Math.max(first, hmToMinutes(start.hm))
  }
  const out: string[] = []
  for (let m = first; m <= last; m += SLOT_MINUTES) out.push(minutesToHm(m))
  return out
}

export type RoomFit = { kind: 'fits' } | { kind: 'partial'; freeMinutes: number } | { kind: 'busy' }

/** How a room's free run from `fromMs` compares to the wanted duration. */
export function roomFit(room: FreeRoomView, fromMs: number, minutes: number): RoomFit {
  if (!room.freeUntil) return { kind: 'busy' }
  const freeMinutes = Math.floor((new Date(room.freeUntil).getTime() - fromMs) / 60_000)
  if (freeMinutes >= minutes) return { kind: 'fits' }
  if (freeMinutes > 0) return { kind: 'partial', freeMinutes }
  return { kind: 'busy' }
}

const RANK = { fits: 0, partial: 1, busy: 2 } as const

/** Rooms that fit first (server order), then the longest partial runs, then busy. */
export function sortRoomsByFit(
  rooms: FreeRoomView[],
  fromMs: number,
  minutes: number,
): Array<{ room: FreeRoomView; fit: RoomFit }> {
  return rooms
    .map((room, i) => ({ room, fit: roomFit(room, fromMs, minutes), i }))
    .sort((a, b) => {
      const r = RANK[a.fit.kind] - RANK[b.fit.kind]
      if (r !== 0) return r
      if (a.fit.kind === 'partial' && b.fit.kind === 'partial') {
        return b.fit.freeMinutes - a.fit.freeMinutes
      }
      return a.i - b.i
    })
    .map(({ room, fit }) => ({ room, fit }))
}
